import { useQuery, useMutation, gql } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminGuard from "@/components/guards/AdminGuard";
import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { secondaryNavigationData } from "@/components/navigation/navigationData";
import Loader from "@/components/views/Loader";
import CreateOrganisationStep from "@/features/onboarding/CreateOrganisationStep";
import {
  CREATE_ORGANISATION_MUTATION,
  LEAVE_ORGANISATION_MUTATION,
  SET_CURRENT_ORGANISATION_MUTATION,
} from "@/graphql/mutations";
import {
  MY_ORGANISATIONS_QUERY,
  GET_CURRENT_USER_QUERY,
} from "@/graphql/queries";

const getNavWithCurrent = (currentName: string) =>
  secondaryNavigationData.map((item) => ({
    ...item,
    current: item.name === currentName,
  }));

export default function OrganisationsSettings() {
  return (
    <AdminGuard>
      <OrganisationsSettingsComponent />
    </AdminGuard>
  );
}

function OrganisationsSettingsComponent() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(MY_ORGANISATIONS_QUERY);
  const { data: userData } = useQuery(GET_CURRENT_USER_QUERY);
  const [modalOpen, setModalOpen] = useState(false);
  const [createOrganisation, { loading: creating, error: createError }] =
    useMutation(CREATE_ORGANISATION_MUTATION, {
      onCompleted: () => {
        setModalOpen(false);
        refetch();
      },
    });

  const [leaveOrganisation, { loading: leaving, error: leaveError }] =
    useMutation(LEAVE_ORGANISATION_MUTATION, {
      onCompleted: () => {
        refetch();
      },
    });

  const [setCurrentOrganisation, { loading: settingOrg, error: setOrgError }] =
    useMutation(SET_CURRENT_ORGANISATION_MUTATION, {
      refetchQueries: [
        { query: MY_ORGANISATIONS_QUERY },
        { query: GET_CURRENT_USER_QUERY },
      ],
      awaitRefetchQueries: true,
    });

  const handleCreateOrg = async (name: string) => {
    await createOrganisation({ variables: { organisation: { name } } });
  };

  const handleLeaveOrg = async (membershipId: string) => {
    await leaveOrganisation({ variables: { membershipId } });
  };

  const handleEditOrg = async (organisationId: string) => {
    navigate(`/settings/organisations/${organisationId}/edit`);
  };

  const handleSetCurrentOrg = async (organisationId: string) => {
    await setCurrentOrganisation({ variables: { organisationId } });
  };

  return (
    <main>
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">Organisations Settings</h1>

        <SecondaryNavigation items={getNavWithCurrent("Organisations")} />

        <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-10">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
                  Organisations
                </h1>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  A list of all the organisations you are a member of including
                  their name, title, email and role.
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => setModalOpen(true)}
                >
                  <svg
                    className="-ml-0.5 mr-1.5 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 20"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 5v10m5-5H5"
                    />
                  </svg>
                  New Organisation
                </button>
              </div>
            </div>
          </div>
          <div className="mt-10">
            {loading && <Loader />}
            {error && (
              <div className="text-red-500">
                Error loading organisations. {error.message}
              </div>
            )}
            {data &&
            data.myOrganisations &&
            data.myOrganisations.nodes.length === 0 ? (
              <div className="flex flex-1 items-center justify-center py-20">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      vectorEffect="non-scaling-stroke"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    No organisations
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating a new organisation.
                  </p>
                  <div className="mt-6"></div>
                </div>
              </div>
            ) : (
              data &&
              data.myOrganisations.nodes && (
                <div className="overflow-x-auto mt-6">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Role
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Joined
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Team
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        ></th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        ></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-600">
                      {data.myOrganisations.nodes.map((org: any) => {
                        const isCurrent =
                          userData?.getCurrentUser?.currentOrganisation?.id ===
                          org.organisation?.id;
                        return (
                          <tr key={org.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-gray-100">
                              {org.organisation?.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300">
                              {org.role?.name.toUpperCase()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300">
                              {new Date(org.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300">
                              {org.teamSize}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300">
                              <div className="flex items-center justify-center gap-1">
                                {isCurrent ? (
                                  <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:ring-green-600/20">
                                    Current
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="inline-flex items-center rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                                    onClick={() =>
                                      handleSetCurrentOrg(org.organisation.id)
                                    }
                                    disabled={settingOrg}
                                  >
                                    {settingOrg ? "Switching..." : "Switch"}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-300">
                              <div className="flex gap-1 justify-end">
                                <button
                                  type="button"
                                  title="Edit"
                                  disabled={org.role?.name !== "admin"}
                                  className="inline-flex items-center disabled:opacity-50 rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                  onClick={() => handleEditOrg(org.id)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  title={
                                    org.role?.name === "admin"
                                      ? "Admins cannot leave their organisation"
                                      : "Leave organisation"
                                  }
                                  disabled={org.role?.name === "admin"}
                                  className="inline-flex items-center disabled:opacity-50 rounded bg-pink-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                  onClick={() => handleLeaveOrg(org.id)}
                                >
                                  Leave
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
            {/* Modal for creating organisation */}
            {modalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-8 w-full max-w-md relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
                    onClick={() => setModalOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                  <CreateOrganisationStep onCreate={handleCreateOrg} />
                  {creating && (
                    <div className="mt-2 text-blue-500 text-center">
                      Creating organisation...
                    </div>
                  )}
                  {createError && (
                    <div className="mt-2 text-red-500 text-center">
                      {createError.message}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </main>
  );
}
